import { forwardRef, useCallback, useEffect, useState } from 'react'

import {
  faDollarSign,
  faHashtag,
  faPercentage,
  IconDefinition,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import isNil from 'lodash.isnil'

import useDisclosure from 'src/hooks/use-disclosure'
import { KeyboardKey } from 'src/types'
import { formatNumber, FormatNumberArgs, NumberFormat } from 'src/utils/number'

import { FormControlProps } from '../FormControl/util'

import Input from './Input'
import { InputBaseProps } from './InputBase'

const MaximumFractionDigits: Record<NumberFormat, number> = {
  [NumberFormat.Currency]: 2,
  [NumberFormat.Float]: 10,
  [NumberFormat.Integer]: 0,
  [NumberFormat.Percentage]: 2,
}

const MinimumFractionDigits: Record<NumberFormat, number> = {
  [NumberFormat.Currency]: 2,
  [NumberFormat.Float]: 0,
  [NumberFormat.Integer]: 0,
  [NumberFormat.Percentage]: 2,
}

const LeftAddonIcon: Record<NumberFormat, IconDefinition> = {
  [NumberFormat.Currency]: faDollarSign,
  [NumberFormat.Float]: faHashtag,
  [NumberFormat.Integer]: faHashtag,
  [NumberFormat.Percentage]: faPercentage,
}

type Props = Omit<InputBaseProps, 'value' | 'onValueChange'> &
  FormControlProps &
  Pick<FormatNumberArgs, 'format' | 'maximumFractionDigits' | 'minimumFractionDigits'> & {
    allowNegative?: boolean
    onValueChange: (value: number | undefined) => void
    value: number | null | undefined
  }
const NumberInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      allowNegative,
      format = NumberFormat.Integer,
      max,
      maximumFractionDigits: propMaximumFractionDigits,
      min,
      minimumFractionDigits: propMinimumFractionDigits,
      onBlur,
      onFocus,
      onKeyDown,
      onValueChange,
      value,
      width = '100%',
      ...props
    },
    ref
  ) => {
    const maximumFractionDigits = propMaximumFractionDigits ?? MaximumFractionDigits[format]
    const minimumFractionDigits = propMinimumFractionDigits ?? MinimumFractionDigits[format]

    const formatNumberForUserInteraction = useCallback(
      (n: number | null | undefined) => {
        if (isNil(n)) {
          return ''
        }
        return formatNumber(n, {
          minimumFractionDigits,
          maximumFractionDigits,
          useGrouping: false,
        })
      },
      [maximumFractionDigits, minimumFractionDigits]
    )

    const [inputText, setInputText] = useState(formatNumberForUserInteraction(value))

    const { isOpen: hasFocus, open: focus, close: blur } = useDisclosure()
    useEffect(() => {
      // Whenever the input loses focus, or the value changes while focus is not in the input, reset the input text to proper formatting
      if (!hasFocus) {
        setInputText(
          formatNumber(value, {
            maximumFractionDigits,
            minimumFractionDigits,
            useGrouping: true,
          })
        )
      }
    }, [setInputText, hasFocus, value, maximumFractionDigits, minimumFractionDigits])

    const handleChange = useCallback(
      (newValue: string | undefined) => {
        const persistChanges = ({ str, num }: { str: string; num: number | undefined }) => {
          if (!isNil(num) && !isNil(max) && num > max) {
            setInputText(formatNumberForUserInteraction(max))
            onValueChange(max)
            return
          }
          setInputText(str)
          onValueChange(num)
        }

        const str = newValue?.trim()
        if (isNil(str) || str === '') {
          persistChanges({ str: '', num: undefined })
          return
        }

        if (str === '-') {
          if (!allowNegative) {
            return
          }
          persistChanges({ str: '-', num: 0 })
          return
        }

        if (str === '-.') {
          if (maximumFractionDigits === 0 || !allowNegative) {
            return
          }
          persistChanges({ str: '-0.', num: 0 })
          return
        }

        if (str === '.') {
          if (maximumFractionDigits === 0) {
            return
          }
          // Auto-convert to neatly formatted decimal value
          persistChanges({ str: '0.', num: 0 })
          return
        }

        const newNumericValue = Number(newValue)
        if (isNaN(newNumericValue)) {
          return
        }

        let sanitizedStr = str.replace(/^(-?)0+([^\.])/, '$1$2') // remove excess leading zeroes
        const negativeSignRegex = allowNegative ? '-?' : ''
        const wholeNumberRegex = '(0|([1-9]\\d*))'
        const fractionRegex =
          maximumFractionDigits === 0 ? '' : `(\\.\\d{0,${maximumFractionDigits}})?`
        const sanitizationRegex = `${negativeSignRegex}${wholeNumberRegex}${fractionRegex}`
        sanitizedStr = sanitizedStr.match(new RegExp(sanitizationRegex))?.[0] ?? ''
        const sanitizedValue = Number(sanitizedStr)
        persistChanges({ str: sanitizedStr, num: sanitizedValue })
      },
      [allowNegative, formatNumberForUserInteraction, max, maximumFractionDigits, onValueChange]
    )

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        focus()
        setInputText(formatNumberForUserInteraction(value))
        onFocus?.(e)
      },
      [focus, formatNumberForUserInteraction, onFocus, value]
    )

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (!isNil(value) && !isNil(min) && value < min) {
          setInputText(formatNumberForUserInteraction(min))
          onValueChange(min)
        }
        blur()
        onBlur?.(e)
      },
      [blur, formatNumberForUserInteraction, min, onBlur, onValueChange, value]
    )

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === KeyboardKey.ArrowUp || e.key === KeyboardKey.ArrowDown) {
          e.preventDefault()
          const step = e.key === KeyboardKey.ArrowUp ? 1 : -1
          const currentValue = value ?? 0
          let newValue = currentValue + step
          if (!allowNegative) {
            newValue = Math.max(0, newValue)
          }
          if (!isNil(min)) {
            newValue = Math.max(min, newValue)
          }
          if (!isNil(max)) {
            newValue = Math.min(max, newValue)
          }
          setInputText(formatNumberForUserInteraction(newValue))
          onValueChange(newValue)
        }
        onKeyDown?.(e)
      },
      [allowNegative, formatNumberForUserInteraction, max, min, onKeyDown, onValueChange, value]
    )

    return (
      <Input
        {...props}
        leftAddon={
          props.leftAddon === undefined ? (
            <FontAwesomeIcon icon={LeftAddonIcon[format]} />
          ) : (
            props.leftAddon
          )
        }
        max={max}
        min={min}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onValueChange={handleChange}
        placeholder={props.placeholder}
        value={inputText}
        ref={ref}
        width={width}
      />
    )
  }
)

export default NumberInput
